/**
 * ENG-8265 — scan an agent's pending-inbound marker dirs for the oldest
 * OUTSTANDING interim promise, and report its age on the responsiveness probe.
 *
 * The predicate itself (what counts as a promise, and why `seen_at` does not) is
 * in `@augmented/core/channels/interim-promise.js` — read that file first; it
 * carries the design rationale and the full list of shapes that must never
 * count. This module is only the filesystem half: walk `<agentHome>/*-pending-
 * inbound/`, parse each marker, and take the earliest `promised_at`.
 *
 * Deliberate differences from the two sibling scans in `responsiveness-probe.ts`:
 *
 *  - **No `sessionStartMs` exclusion.** `oldestLivePendingInboundMtimeMs` skips
 *    markers older than the current session because a leftover marker cannot
 *    prove *this* session is failing to drain. Applying that here would have made
 *    ENG-8265 a no-op in the incident that motivated it: stirling's promise was
 *    made before two mid-turn respawns, so a session-relative measure would
 *    discard the only evidence. A promise survives a restart precisely because
 *    the user's expectation does.
 *  - **Age from the marker's `promised_at`, not its mtime.** Every in-place
 *    rewrite (a reaction, a `read_thread`, a replay-payload attach) bumps mtime;
 *    none of them discharge a promise.
 *  - **A malformed marker is excluded, not included.** The live wedge scan treats
 *    an unreadable marker as LIVE so a corrupt file can never mask a real wedge.
 *    Here the asymmetry is reversed: this signal opens an alert, and a false alert
 *    about a healthy conversation is the expensive outcome, so an unparseable
 *    marker fails quiet.
 */
import { readFileSync, readdirSync } from 'node:fs';
import { dirname, join } from 'node:path';

import {
  interimPromiseAgeSeconds,
  outstandingPromiseAtMs,
  type InterimPromiseMarkerFields,
} from '@augmented/core/channels/interim-promise.js';

/**
 * Earliest `promised_at` (ms epoch) across every marker under `agentHomeDir` that
 * carries an outstanding promise, or `null` when the agent owes none.
 *
 * `null` is the healthy steady state and the reason the probe field is OMITTED
 * rather than sent as 0 — the absent-vs-zero contract every other probe counter
 * follows (a 0 would read as "promise made 0s ago", i.e. permanently healthy, and
 * would re-mask the failure on mixed-version fleets).
 */
export function oldestOutstandingPromiseAtMs(agentHomeDir: string): number | null {
  let oldest: number | null = null;
  let entries;
  try {
    entries = readdirSync(agentHomeDir, { withFileTypes: true });
  } catch {
    return null; // agent home missing — nothing to report
  }
  for (const entry of entries) {
    // Same directory contract as the sibling scans: only `<channel>-pending-
    // inbound/`. The `-stale` / `-dead-letter` siblings deliberately do not match
    // (a parked-aside marker is no longer an outstanding promise), and hidden
    // entries are MCP bookkeeping (`.markers/`, `.processed/`), never markers.
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound')) continue;
    const dir = join(agentHomeDir, entry.name);
    let files;
    try {
      files = readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.isFile() || file.name.startsWith('.')) continue;
      let marker: InterimPromiseMarkerFields;
      try {
        marker = JSON.parse(readFileSync(join(dir, file.name), 'utf8')) as InterimPromiseMarkerFields;
      } catch {
        // Vanished between readdir and read (a final reply landed — the happy
        // path), or unparseable. Either way: not a promise. See the header note
        // on why this fails quiet where the wedge scan fails loud.
        continue;
      }
      const promisedAt = outstandingPromiseAtMs(marker);
      if (promisedAt === null) continue;
      if (oldest === null || promisedAt < oldest) oldest = promisedAt;
    }
  }
  return oldest;
}

/**
 * Age (seconds) of the oldest outstanding promise under `agentHomeDir`, or `null`
 * when there is none. This is the value the responsiveness probe reports as
 * `promise_outstanding_oldest_age_seconds`.
 */
export function outstandingPromiseOldestAgeSeconds(
  agentHomeDir: string,
  now: Date = new Date(),
): number | null {
  const promisedAt = oldestOutstandingPromiseAtMs(agentHomeDir);
  if (promisedAt === null) return null;
  return interimPromiseAgeSeconds(promisedAt, now.getTime());
}

/**
 * Convenience form keyed on an agent code name, for callers that only have the
 * pane-log path convention to hand. `paneLogPathFn` is injected rather than
 * imported so this module stays free of `responsiveness-probe.ts`'s import graph
 * (and testable without a real agent home).
 */
export function outstandingPromiseOldestAgeSecondsForAgent(
  codeName: string,
  paneLogPathFn: (codeName: string) => string,
  now: Date = new Date(),
): number | null {
  return outstandingPromiseOldestAgeSeconds(dirname(paneLogPathFn(codeName)), now);
}
